import { Progress, Typography, Space, Tag, Alert } from 'antd';
import { useEffect, useRef } from 'react';
import { useJobPoll } from '@/hooks/useTranslateBatch';
import { useTranslateJobsStore } from '@/stores/translateJobsStore';

const { Text, Paragraph } = Typography;

interface Props {
  jobId: string;
  onTerminal?: (status: 'success' | 'partial' | 'failed', itemsTranslated: number) => void;
}

export function AiTranslateProgress({ jobId, onTerminal }: Props) {
  const q = useJobPoll(jobId);
  const untrack = useTranslateJobsStore((s) => s.untrack);

  const status = q.data?.status;
  const items = q.data?.items_translated ?? 0;

  // Fire the terminal callback exactly once per job. Without this guard the
  // effect re-runs whenever onTerminal changes identity (it's an inline arrow in
  // the parent), and if onTerminal triggers a re-render (e.g. refetching the
  // topic) it loops — hammering the API into a 429.
  const firedFor = useRef<string | null>(null);
  useEffect(() => {
    firedFor.current = null; // reset when the job changes
  }, [jobId]);
  useEffect(() => {
    if (
      (status === 'success' || status === 'partial' || status === 'failed') &&
      firedFor.current !== jobId
    ) {
      firedFor.current = jobId;
      untrack(jobId);
      onTerminal?.(status, items);
    }
  }, [status, items, jobId, untrack, onTerminal]);

  if (!q.data) return <Text type="secondary">Connecting to job…</Text>;
  const j = q.data;
  const pct = j.total_batches === 0 ? 0 : Math.round((j.completed_batches / j.total_batches) * 100);

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space>
        <Tag
          color={
            j.status === 'success'
              ? 'success'
              : j.status === 'failed'
                ? 'error'
                : j.status === 'partial'
                  ? 'warning'
                  : 'processing'
          }
        >
          {j.status}
        </Tag>
        <Text>
          {j.completed_batches} / {j.total_batches} batches · {j.items_translated} items · $
          {j.cost_usd.toFixed(4)}
        </Text>
      </Space>
      <Progress
        percent={pct}
        status={j.status === 'failed' ? 'exception' : j.status === 'success' ? 'success' : 'active'}
      />
      {j.error_message && <Alert type="error" message={j.error_message} showIcon />}
      {j.status === 'success' && j.items_translated === 0 && (
        <Alert
          type="info"
          showIcon
          message="Nothing to translate — already up to date"
          description='This language already has translations for every field. Re-run with "Overwrite all" to replace them.'
        />
      )}
      <Paragraph
        style={{
          background: '#0c0c0c',
          color: '#d4d4d4',
          padding: 12,
          borderRadius: 4,
          maxHeight: 280,
          overflowY: 'auto',
          fontFamily: 'ui-monospace, monospace',
          fontSize: 12,
          whiteSpace: 'pre-wrap',
        }}
      >
        {(j.log ?? []).join('\n')}
      </Paragraph>
    </Space>
  );
}
